import { useEffect, useState } from 'react';
import { useApiClient } from '@/lib/api';
import {
  fetchSignaturePhysicalValues,
  type SignaturePhysicalValuesData,
} from '@/lib/case_signatures';

export interface SignaturePhysicalValuesState {
  data: SignaturePhysicalValuesData | null;
  /** true = backend respondeu 422 (imagem sem base para extração) —
   * estado "indisponível" honesto (ADR-0038 §F). */
  unavailable: boolean;
  /** true = falha técnica (rede/autenticação/5xx) — NUNCA apresentada como
   * "indisponível": indisponibilidade é estado forense do material, falha
   * operacional não pode se passar por ele (F-E26.F3-02). */
  error: boolean;
  loading: boolean;
}

const IDLE: SignaturePhysicalValuesState = {
  data: null,
  unavailable: false,
  error: false,
  loading: false,
};

/**
 * Fetch autenticado da matriz de valores físicos (E26.F3/ADR-0038).
 * Espelha `useSignatureGraphicGenesis` (payload JSON, sem object URL).
 */
export function useSignaturePhysicalValues(
  caseId: string,
  signatureId: string | undefined,
): SignaturePhysicalValuesState {
  const client = useApiClient();
  const [state, setState] = useState<SignaturePhysicalValuesState>(IDLE);

  useEffect(() => {
    if (!signatureId || !caseId) {
      setState(IDLE);
      return;
    }
    const resolvedSignatureId = signatureId;
    let cancelled = false;
    setState({ data: null, unavailable: false, error: false, loading: true });
    async function load() {
      try {
        const data = await fetchSignaturePhysicalValues(client, caseId, resolvedSignatureId);
        if (cancelled) return;
        if (data === null) {
          setState({ data: null, unavailable: true, error: false, loading: false });
          return;
        }
        setState({ data, unavailable: false, error: false, loading: false });
      } catch {
        if (!cancelled) setState({ data: null, unavailable: false, error: true, loading: false });
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [client, caseId, signatureId]);

  return state;
}
